import type { PlayerResponsibleGaming } from "@utopia-well-done-ltd/contracts";
import { useEffect, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { asMoment } from "../format.js";
import { useTranslation } from "../i18n.js";

const limit = (
  value: { amount: string; period: string } | null,
  t: (value: string) => string,
): string =>
  value
    ? `${(BigInt(value.amount) / 100n).toString()}.${(BigInt(value.amount) % 100n).toString().padStart(2, "0")} / ${value.period}`
    : t("Not set");

export function ResponsibleGaming({ playerId }: { playerId: string }) {
  const t = useTranslation();
  const [controls, setControls] = useState<PlayerResponsibleGaming | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .playerResponsibleGaming(playerId, controller.signal)
      .then(({ controls: found }) => setControls(found))
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) {
          setError(caught instanceof ApiError ? caught.message : "Could not load player controls.");
        }
      });
    return () => controller.abort();
  }, [playerId]);

  if (error)
    return (
      <p className="banner" role="alert">
        {error}
      </p>
    );
  if (!controls) return <p className="empty">Loading…</p>;

  return (
    <>
      <div className="facts">
        <div className="fact">
          <div className="fact-label">{t("Self-exclusion")}</div>
          <div className="fact-value">
            {controls.selfExclusionUntil
              ? `${t("Until")} ${asMoment(controls.selfExclusionUntil)}`
              : t("None")}
          </div>
        </div>
        <div className="fact">
          <div className="fact-label">{t("Cool-off")}</div>
          <div className="fact-value">
            {controls.coolOffUntil ? `${t("Until")} ${asMoment(controls.coolOffUntil)}` : t("None")}
          </div>
        </div>
        <div className="fact">
          <div className="fact-label">{t("Deposit limit")}</div>
          <div className="fact-value mono-12">{limit(controls.depositLimit, t)}</div>
        </div>
        <div className="fact">
          <div className="fact-label">{t("Loss limit")}</div>
          <div className="fact-value mono-12">{limit(controls.lossLimit, t)}</div>
        </div>
        <div className="fact">
          <div className="fact-label">{t("Wager limit")}</div>
          <div className="fact-value mono-12">{limit(controls.wagerLimit, t)}</div>
        </div>
        <div className="fact">
          <div className="fact-label">{t("Reality check")}</div>
          <div className="fact-value">
            {controls.realityCheckMinutes
              ? `${controls.realityCheckMinutes} ${t("minutes")}`
              : t("Not set")}
          </div>
        </div>
      </div>

      {controls.pending.length > 0 ? (
        <>
          <div className="card-bar">
            <span className="label-14-strong">{t("Pending changes")}</span>
            <span className="label-13 faint">{t("cooling-off period")}</span>
          </div>
          <table className="mobile-cards">
            <thead>
              <tr>
                <th>{t("Limit")}</th>
                <th>{t("New value")}</th>
                <th>{t("Requested")}</th>
                <th>{t("Effective")}</th>
              </tr>
            </thead>
            <tbody>
              {controls.pending.map((change) => (
                <tr key={change.id}>
                  <td className="label-13" data-label={t("Limit")}>
                    {change.kind}
                  </td>
                  <td className="mono-12" data-label={t("New value")}>
                    {change.newLimit && change.newPeriod
                      ? limit({ amount: change.newLimit, period: change.newPeriod }, t)
                      : t("No limit")}
                  </td>
                  <td className="label-13 dim" data-label={t("Requested")}>
                    {asMoment(change.requestedAt)}
                  </td>
                  <td className="label-13 dim" data-label={t("Effective")}>
                    {asMoment(change.effectiveAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </>
  );
}
